import * as React from 'react';
import { GroupServiceGetResponse } from '../../../services/api';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import ModalButtons from '../../ui/modals/ModalButtons';
import FileUploader from '../../uploads/FileUploader';
import FileSummary from '../../uploads/FileSummary';
import { useAppDispatch, useAppSelector } from '../../../store';
import { fetchGroup, submitRerateRequest } from '../../../store/group';
import { APIStatus, GroupStatus } from '../../../utils/types';
import { Message } from 'primereact/message';
import { canMakeRequests } from '../../../utils/groupList';
import { UserRole } from '../../../store/auth';
import { flattenTimePeriodDate } from '../../../utils/date';

export default function RerateRequest({ group, onActionRerate }: { group: GroupServiceGetResponse, onActionRerate: () => void }) {
  const dispatch = useAppDispatch();
  const [isModalVisible, setIsModalVisible] = React.useState<boolean>(false);
  const [requestStatus, setRequestStatus] = React.useState<APIStatus | null>(null);

  const { role } = useAppSelector((state) => state.auth);
  const [files, setFiles] = React.useState<File[]>([]);
  const [note, setNote] = React.useState<string>('');

  const formIsValid = React.useMemo<boolean>(() => {
    let isValid = true;

    if (!note.length) isValid = false;

    return isValid;
  }, [files, note]);

  async function submitRequest() {
    try {
      setRequestStatus(APIStatus.LOADING);
      await submitRerateRequest({
        file: files[0],
        note,
        policyNumber: group.policyDetailModel?.policyNumber!,
        timePeriodDate: flattenTimePeriodDate(group.policyDetailModel?.timePeriodDate!),
      });

      setRequestStatus(APIStatus.COMPLETE);

      // refresh the group
      dispatch(fetchGroup({
        policyNumber: group.policyDetailModel?.policyNumber!,
        timePeriodDate: flattenTimePeriodDate(group.policyDetailModel?.timePeriodDate!),
      }));
      hideModal();
    } catch {
      setRequestStatus(APIStatus.ERROR);
    }
  }


  function ModalHeader() {
    return (
      <>
        <div className="leading-none">Request rerate</div>
        <div className="text-lg font-normal">Provide supporting documentation for actuarial review</div>
      </>
    )
  }

  function FilesList() {
    return (
      <div className="flex flex-col gap-2 px-5 pt-2 pb-4 rounded-lg bg-grey-2">
        <h2 className="font-bold">Files uploaded</h2>
        <div className="grid grid-cols-2 gap-4">
          {files.map((file, index) => {
            return <FileSummary key={index} className="p-2 bg-white border rounded-md border-grey-5" removable />
          })}
        </div>
      </div>
    )
  }

  function hideModal() {
    setIsModalVisible(false);
    setNote('');
    setFiles([]);
  }

  function scrollToRerateResponseForm() {
    const formElement = document.querySelector('[data-rerate-response-form]');
    formElement?.scrollIntoView();
  }

  function handleRerateButton() {
    if (group.requestStatus === GroupStatus.RERATE_REQUESTED) {
      scrollToRerateResponseForm();
      return;
    }

    onActionRerate();
  }

  if (role === UserRole.ACTUARY) {
    return (
      <Button severity="secondary" label="action rerate" rounded onClick={() => handleRerateButton()} />
    )
  }

  return (
    <>
      <Button
        rounded
        severity="secondary"
        onClick={() => setIsModalVisible(true)}
        label={group.requestStatus === GroupStatus.RERATE_REQUESTED ? "rerate requested" : (role === UserRole.ACTUARY ? "action rerate" : "request rerate")}
        disabled={!canMakeRequests(group)}
      />
      <Dialog
        header={ModalHeader}
        visible={isModalVisible}
        onHide={hideModal}
        footer={
          <ModalButtons
            onSave={() => submitRequest()}
            onCancel={() => setIsModalVisible(false)}
            isLoading={requestStatus === APIStatus.LOADING}
            isFormValid={formIsValid}
          />
        }
      >
        <div className="flex flex-col gap-4">
          <FileUploader files={files} filesListTemplate={FilesList} onAddFile={(e) => setFiles(e)} />
          <div className="flex flex-col gap-2">
            <p className="font-bold">Supporting notes</p>
            <InputTextarea value={note} onChange={(e) => setNote(e.target.value)} rows={6} placeholder="Add supporting notes" />
          </div>

          {requestStatus === APIStatus.ERROR && (
            <Message severity="error" text="There was an error submitting your request, please try again" />
          )}          
        </div>
      </Dialog>
    </>
  )
}